import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Meeting, MeetingBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaTemplate, MeetingAgendaTemplateBreezeModel, TemplateType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IDxSelectBoxValueChangedEvent } from "@common/ux/dx.types";
import { DxSelectBoxComponent } from "devextreme-angular";
import { lastValueFrom } from "rxjs";

@Component({
    selector: "adapt-select-meeting-or-template",
    templateUrl: "./select-meeting-or-template.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SelectMeetingOrTemplateComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() public organisation?: Organisation;
    @Input() public team?: Team;
    @Output() public meetingOrTemplateChange = new EventEmitter<MeetingAgendaTemplate | Meeting>();

    public items: (MeetingAgendaTemplate | Meeting)[] = [];

    public meetingOrTemplate?: MeetingAgendaTemplate | Meeting;
    @ViewChild(DxSelectBoxComponent) private selectBoxComponent?: DxSelectBoxComponent;

    constructor(
        private commonDataService: CommonDataService,
    ) {
        super();
    }

    public async ngOnInit(): Promise<void> {
        await this.fetchMeetingsAndAgendaTemplates();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (changes.organisation || changes.team) {

            this.meetingOrTemplate = undefined;
            this.meetingOrTemplateChange.emit(this.meetingOrTemplate);
            this.selectBoxComponent?.instance.reset();

            await this.fetchMeetingsAndAgendaTemplates();
        }
    }

    private async fetchMeetingsAndAgendaTemplates() {
        if (!this.organisation) {
            return;
        }

        const predicate = new MethodologyPredicate<MeetingAgendaTemplate>("organisationId", "==", this.organisation.organisationId);
        const templatePredicate = predicate.clone()
            .and(new MethodologyPredicate("templateType", "==", TemplateType.User));
        const meetingAgendaTemplates = await lastValueFrom(this.commonDataService.getByPredicate(MeetingAgendaTemplateBreezeModel, templatePredicate));
        const meetings = await lastValueFrom(this.commonDataService.getByPredicate(MeetingBreezeModel, predicate));

        this.items = [
            ...meetingAgendaTemplates.filter((template) => this.team ? template.teamId === this.team?.teamId : template.teamId === null),
            ...meetings.filter((meeting) => this.team ? meeting.teamId === this.team?.teamId : false)
                .sort((a, b) => new Date(b.meetingDateTime).getTime() - new Date(a.meetingDateTime).getTime()),
        ];
    }

    public isMeeting(item: any): boolean {
        return item && "meetingId" in item;
    }

    public getDisplayText = (item: Meeting | MeetingAgendaTemplate | null): string => {
        if (!item) {
            return "";
        }

        return this.isMeeting(item)
            ? `(meeting) ${item.name} at ${(item as Meeting).meetingDateTime.toLocaleString()}`
            : `(template) ${item.name}`;
    };

    public async onMeetingOrTemplateChanged(e: IDxSelectBoxValueChangedEvent<MeetingAgendaTemplate | Meeting>) {
        if (e.value === e.previousValue) {
            return;
        }

        this.meetingOrTemplate = e.value;
        this.meetingOrTemplateChange.emit(this.meetingOrTemplate);
    }
}
